import firebaseConfig from "./config"
import CareerPlan from "../careers/CareerPlan"
import School from "../schools/School"
import Major from "../majors/Major"
import { v4 } from 'uuid';
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/functions'
import 'firebase/compat/storage'
import { ImportResponseFailure, ImportResponseSuccess } from "../admin/AdminFirebaseFunctions";

/**
 * Add constant for the time window of general notifications (7 days).
 *
 * @type {number}
 */
const newContentAlertTimeFrame = 604800000

/**
 * Set default expiration time of 6 hours.
 *
 * @type {number}
 */
const sessionExpirationTime = 21600000

class Firebase {
  constructor() {
    if (!firebaseInstance) {
      firebase.initializeApp(firebaseConfig)
      this.app = firebase
      this.auth = this.app.auth()
      this.db = this.app.firestore()
      this.functions = this.app.functions()
      this.storage = this.app.storage()
    }

    // Determine if the current subdomain is the demo subdomain, used for blocking certain features
    const subdomain = typeof window !== "undefined" ? window.location.hostname.split(".")[0] : null
    this.isDemoSubdomain = subdomain === "demo"
  }

  /**
   * Reusable client-side guard for disabling actions on demo subdomain.
   * Returns true when blocked.
   *
   * @param {Object} options
   * @param {string} options.message
   * @param {boolean} options.showAlert
   * @returns {boolean}
   */
  blockIfDemoSubdomain({ message = "This action is disabled on the demo", showAlert = true } = {}) {
    if (!this.isDemoSubdomain) {
      return false
    }

    if (showAlert && typeof window !== "undefined" && typeof window.alert === "function") {
      window.alert(message)
    }

    return true
  }

  /**
   * Subscribe to global config data
   * @param onSnapshot
   * @returns {Promise<null|*>}
   */
  async getGlobalSiteConfig({ onSnapshot }) {
    const user = this.auth.currentUser
    if (user) {
      return this.db.collection("globalSiteConfig").doc("CONFIG").onSnapshot(onSnapshot)
    } else {
      return null
    }
  }

  /**
   * Subscribe to userActivity data
   * @param onSnapshot
   * @returns {Promise<null|*>}
   */
  async subscribeToUserActivities({ onSnapshot }) {
    const user = this.auth.currentUser
    if (user) {
      return this.db.collection("userActivities").doc(user.uid).onSnapshot(onSnapshot)
    } else {
      return null
    }
  }

  /** Get User Activity
   */
  async getUserActivities(userId) {
    try {
      const resp = await this.db.collection("userActivities").doc(userId).get()
      if (resp.exists) {
        return resp.data()
      } else {
        return null
      }
    } catch (error) {
      return error
    }
  }

  /**
   * Subscribe to userNotifications data
   * @param onSnapshot
   * @returns {Promise<null|*>}
   */
  async subscribeToUserNotifications({ onSnapshot }) {
    const user = this.auth.currentUser
    if (user) {
      return this.db.collection("userNotifications").doc(user.uid).onSnapshot(onSnapshot)
    } else {
      return null
    }
  }

  /**
   * Subscribe to userNotifications data
   * @param onSnapshot
   * @returns {Promise<null|*>}
   */
  async subscribeToUserPointTotal({ onSnapshot }) {
    const user = this.auth.currentUser
    if (user) {
      return this.db.collection("pointTotals").doc(user.uid).onSnapshot(onSnapshot)
    } else {
      return null
    }
  }

  /**
   * Retrieve a user's membership information
   *
   * @returns {Promise<null|*>}
   */
  async getUserMembership() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    const doc = await this.db.collection("memberships").doc(user.uid).get()
    if (doc.exists) {
      return doc.data()
    } else {
      return null
    }
  }

  /**
   * Retrieve a user's visit information
   *
   * @returns {Promise<null|*>}
   */
  async getVisitCount() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    const doc = await this.db.collection("userMetrics").doc(user.uid).get()
    if (doc.exists) {
      const data = doc.data()
      return data.siteVisitCount
    } else {
      return null
    }
  }

  /**
   * Retrieve a user's assessment information
   *
   * @returns {Promise<null|*>}
   */
  async getAssessmentRecord() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    const doc = await this.db.collection("assessments").doc(user.uid).get()
    if (doc.exists) {
      return doc.data()
    } else {
      return null
    }
  }

  /**
   * Retrieve a user's activity information
   *
   * @returns {Promise<null|*>}
   */
  async getActivityRecord() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }
    const doc = await this.db.collection("userActivities").doc(user.uid).get()
    if (doc.exists) {
      return doc.data()
    } else {
      return []
    }
  }

  /**
   * Retrieve a user's response record for a specific quest
   *
   * @returns {Promise<null|*>}
   */
  async getDailyQuestRecord(questId) {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }
    const doc = await this.db.collection("userQuests").doc(`${questId}-${user.uid}`).get()
    if (doc.exists) {
      return doc.data()
    } else {
      return null
    }
  }

  /**
   *
   */
  async getPast5DaysQuestCompletionCount() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }
    const currentTime = new Date()
    const fiveDaysAgo = new Date()
    fiveDaysAgo.setDate(currentTime.getDate() - 5)

    const docs = await this.db.collection("userQuests").where("createdDate", ">=", fiveDaysAgo).where("userId", "==", user.uid).get()

    const questRecords = []

    docs.forEach((doc) => {
      questRecords.push({ id: doc.id, ...doc.data() })
    })

    return questRecords.length
  }

  /**
   * remove a user's membership information
   */
  async removeUserMembership({ userId, onSnapshot }) {
    const deleteSubscriptionCallable = this.functions.httpsCallable("deleteSubscription")
    const callableResult = await deleteSubscriptionCallable()
    return callableResult.data
  }

  /**
   * Subscribe to user profile data for current user
   * @param onSnapshot
   * @returns {() => void}
   */
  getUserProfile({ userId, onSnapshot }) {
    const user = this.auth.currentUser
    if (user) {
      return this.db.collection("users").doc(user.uid).onSnapshot(onSnapshot)
    } else {
      return null
    }
  }

  /**
   * Fetch the name, school code, and peterson's ID (if it exists) of all
   * schools the current user is a member of.
   *
   * @return {Promise<{sch_code: string, name: string, petersons_institution_id: string}[]>}
   * an array of school data, null if the request fails because the current user
   * is not authenticated yet, or an empty array if the request fails for any
   * other reason
   */
  async getUserSchoolMemberships() {
    try {
      const schoolMembershipFetcher = this.functions.httpsCallable("getUserSchoolMemberships")
      const memberships = await schoolMembershipFetcher()
      return memberships.data || null
    } catch (e) {
      if (e.code === "functions/permission-denied") {
        return null
      }
      console.error("error in getUserSchoolMemberships:", e, e.code)
      return []
    }
  }

  /**
   * Retrieve the current user's profile as a static object, rather
   * than subscribing to the data.
   *
   * @return {Promise<null|*>}
   */
  async getProfile() {
    const user = this.auth.currentUser
    try {
      const snapshot = await this.db.collection("users").doc(user.uid).get()

      if (!snapshot.exists) {
        return null
      } else {
        return snapshot.data()
      }
    } catch (e) {
      return null
    }
  }

  async getCareerData(title, locale) {
    try {
      const snapshot = await this.db
        .collection(locale === "en-US" ? "wages" : "wagesEs")
        .where("docId", "==", title)
        .get()
      if (snapshot.empty) {
        return null
      } else {
        const career = []
        snapshot.forEach((doc) => {
          career.push({
            ...doc.data(),
          })
        })
        return career[0]
      }
    } catch (e) {
      return null
    }
  }

  async createNewCareer(careerTitle, locale) {
    try {
      const data = {
        hardSkills: [],
        salaryData: [],
        softSkills: [],
        title: careerTitle,
        docId: careerTitle.toLowerCase(),
      }
      const response = {
        success: true,
        message: "saved",
      }
      const checkDuplicate = await this.getCareerData(careerTitle, locale)
      if (checkDuplicate && checkDuplicate.title === careerTitle) {
        response.message = `Career with name ${careerTitle} already exist`
        response.success = false
        return response
      }
      await this.db.collection("wages").doc(careerTitle).set(data)
      return response
    } catch (e) {
      return e
    }
  }

  async getSchoolData(studentId) {
    try {
      const snapshot = await this.db.collection("schoolData").doc(studentId).get()

      if (!snapshot.exists) {
        return null
      } else {
        return snapshot.data()
      }
    } catch (e) {
      return null
    }
  }

  async getOtherUserPointTotal(studentId) {
    const getOtherUserPointTotal = this.functions.httpsCallable("getOtherUserPointTotal")

    try {
      const res = await getOtherUserPointTotal({ userId: studentId })
      return res?.data
    } catch (e) {
      return 0
    }
  }

  async getCampusList(schCode) {
    const fetchCampus = this.functions.httpsCallable("onGetCampusBySchoolCode")
    try {
      const resp = await fetchCampus({ sch_code: schCode })
      return resp?.data
    } catch (error) {
      return error
    }
  }

  async getDepartmentList(schCode) {
    const fetchDepartment = this.functions.httpsCallable("onGetDepartmentBySchoolCode")
    try {
      const resp = await fetchDepartment({ sch_code: schCode })
      return resp?.data
    } catch (error) {
      return error
    }
  }

  async groupsListCreatedByUser() {
    const grouplist = this.functions.httpsCallable("getGroupsByUserId")
    try {
      const resp = await grouplist()
      return resp
    } catch (error) {
      return error
    }
  }

  async getGroupByRoleAndUserId(data) {
    const getGroup = this.functions.httpsCallable("onGetGroupByRoleAndUserId")
    try {
      const resp = await getGroup(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async addNewGroup(data) {
    const groupAdd = this.functions.httpsCallable("createGroup")
    try {
      const resp = await groupAdd(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async associateStudentToGroup(payload) {
    const groupsResponse = this.functions.httpsCallable("onAddUsersToGroup")
    try {
      const resp = await groupsResponse(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getAllUserListing(data) {
    const getAllUsersList = this.functions.httpsCallable("onGetAllUsers")
    try {
      const resp = await getAllUsersList(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async getAllUserFromGroup(data) {
    const getAllUsersListFromGroup = this.functions.httpsCallable("onGetUsersFromGroup")
    try {
      const resp = await getAllUsersListFromGroup(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async getActiveGroupMembersById(type) {
    const getActiveGroupMembersById = this.functions.httpsCallable("onGetActiveGroupMembersById")
    try {
      const resp = await getActiveGroupMembersById({ type })
      return resp
    } catch (error) {
      return error
    }
  }

  async deleteUsersFromGroup(payload) {
    const deleteUsersFromGroupCallable = this.functions.httpsCallable("onDeleteUsersFromGroup")
    const resp = await deleteUsersFromGroupCallable(payload)
    return resp.data
  }

  async editGroupName(data) {
    const groupEdit = this.functions.httpsCallable("onUpdateGroupNameById")
    try {
      const resp = await groupEdit(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async deleteGroup(data) {
    const groupDelete = this.functions.httpsCallable("onDeleteGroups")
    try {
      const resp = await groupDelete(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async getCollaboratorsFromGroup(data) {
    const getCollaborators = this.functions.httpsCallable("onGetCollaboratorsFromGroup")
    try {
      const resp = await getCollaborators(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async addCollaboratorsToGroup(data) {
    const addCollaborators = this.functions.httpsCallable("onAddCollaboratorsToGroup")
    try {
      const resp = await addCollaborators(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async deleteCollaboratorsFromGroup(data) {
    const deleteCollaborators = this.functions.httpsCallable("onDeleteCollaboratorsFromGroup")
    try {
      const resp = await deleteCollaborators(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async transferGroupOwner(data) {
    const transferOwner = this.functions.httpsCallable("onTransferGroupOwner")
    try {
      const resp = await transferOwner(data)
      return resp
    } catch (error) {
      return error
    }
  }

  async getAllRaisecListing() {
    const getRiasecList = this.functions.httpsCallable("onGetRIASEC")
    try {
      const resp = await getRiasecList()
      return resp
    } catch (error) {
      return error
    }
  }

  async updateCareerSkillsAndStateData(title, data) {
    const careerRef = this.db.collection("wages").doc(title)
    const updateWagesEs = this.functions.httpsCallable("setCareerDataInEs")
    try {
      await careerRef.update(data)
      await updateWagesEs(data)
      return true
    } catch (error) {
      return error
    }
  }

  async getAllCareerSkillsAndStateData() {
    try {
      const snapshot = await this.db.collection("wages").get()
      const d = []
      snapshot.forEach((doc) => {
        d.push(doc.data())
      })
      return d
    } catch (e) {
      return null
    }
  }

  async getDailyQuestListPerUser(userId) {
    const doc = await this.db.collection("userQuests").where("userId", "==", userId).get()
    try {
      if (doc.empty) {
        return []
      } else {
        const quest = []
        doc.forEach((doc) => {
          quest.push({
            ...doc.data(),
          })
        })
        return quest
      }
    } catch (error) {
      return error
    }
  }
  async getOtherUserCareerPlans(userId) {
    const getOtherUserCareerPlans = this.functions.httpsCallable("getOtherUserCareerPlans")

    try {
      const res = await getOtherUserCareerPlans({ userId: userId })
      return res?.data
    } catch (error) {
      return error
    }
  }

  async getFacultyListing(payload) {
    const facultyListing = this.functions.httpsCallable("facultyListings")
    try {
      const resp = await facultyListing(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getStudentListing(payload) {
    const studentListing = this.functions.httpsCallable("studentListings")
    try {
      const resp = await studentListing(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getProfileDetails(userId) {
    const profileDetails = this.functions.httpsCallable("getProfileDetails")
    try {
      return await profileDetails({ userId: userId })
    } catch (error) {
      return error
    }
  }

  async addUser(payload) {
    const addUser = this.functions.httpsCallable("onAddUser")
    try {
      const resp = await addUser(payload)
      return resp
    } catch (error) {
      return error
    }
  }
  async editUser(payload) {
    const editUser = this.functions.httpsCallable("onEditUser")
    try {
      const resp = await editUser(payload)
      return resp
    } catch (error) {
      return error
    }
  }
  async OnDeleteUser(payload) {
    const OnDeleteUser = this.functions.httpsCallable("onDeleteUser")
    try {
      const resp = await OnDeleteUser(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async studentActivitySnapshot(payload) {
    const getStudentActivity = this.functions.httpsCallable("onUserActivity")
    try {
      const resp = await getStudentActivity(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getFacultyQueryResponse(payload) {
    const facultyQuery = this.functions.httpsCallable("onGetFacultyReport")
    try {
      const resp = await facultyQuery(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getStudentQueryResponse(payload) {
    const studentQuery = this.functions.httpsCallable("onGetStudentReport")
    try {
      const resp = await studentQuery(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  /**
   * Fetch a curriculum report for a series of modules or a program.
   * @param {Object} payload
   * @param {number} [payload.moduleIds]
   * @param {number} [payload.programId]
   * @param {number} [payload.userId]
   * @param {"rows"|"excel"} [payload.outputFormat]
   * @param {Object} [payload.filters]
   * @param {number} [payload.pageSize]
   * @param {number} [payload.pgNo]
   * @returns {Promise<{meta: Object, rows: Object[]}>} for outputFormat "rows"
   */
  async getCurriculumReport(payload) {
    const curriculumReport = this.functions.httpsCallable("onGetCurriculumReport", { timeout: 540000 })
    const resp = await curriculumReport(payload)
    return resp
  }

  /**
   * Fetch a curriculum report as an excel workbook (full responses) for a
   * module, given the same filters as getCurriculumReport.
   * @param {Object} payload
   * @returns {Promise<Blob>} excel file in blob form
   */
  async getCurriculumReportExcel(payload) {
    const resp = await this.getCurriculumReport({ ...payload, outputFormat: "excel" })
    const base64Data = resp.data
    const decoded = await fetch(`data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Data}`)
    return await decoded.blob()
  }

  async getAllReports(payload) {
    const reports = this.functions.httpsCallable("onGetAllReports")
    try {
      const resp = await reports(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async saveReports(payload) {
    const saveReports = this.functions.httpsCallable("onSaveReport")
    try {
      const resp = await saveReports(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async saveSchedule(payload) {
    const saveSchedule = this.functions.httpsCallable("onSaveScheduler")
    try {
      const resp = await saveSchedule(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async editSchedule(payload) {
    const editSchedule = this.functions.httpsCallable("onEditScheduler")
    try {
      const resp = await editSchedule(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getSchedularInfo(payload) {
    const getSchedular = this.functions.httpsCallable("onGetSchedulerById")
    try {
      const resp = await getSchedular(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getReports(payload) {
    const exportReport = this.functions.httpsCallable("onGetReportById")
    try {
      const resp = await exportReport(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async deleteReport(payload) {
    const deleteReport = this.functions.httpsCallable("onDeleteReportById")
    try {
      const resp = await deleteReport(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async deleteSchedule(payload) {
    const deleteSchedule = this.functions.httpsCallable("onDeleteSchedulerById")
    try {
      const resp = await deleteSchedule(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async getSchedulers(payload) {
    const schedulers = this.functions.httpsCallable("onGetSchedulers")
    try {
      const resp = await schedulers(payload)
      return resp
    } catch (error) {
      return error
    }
  }

  async importUsers(fileData, fileName, type, groupId, schoolId) {
    // disable user import on the demo subdomain
    if (this.blockIfDemoSubdomain({ message: "User import is disabled on the demo" })) return null

    const importer = this.functions.httpsCallable("onImportUser")
    try {
      const response = await importer({ fileData, fileName, type, groupId, schoolId })
      return new ImportResponseSuccess(response.data)
    } catch (e) {
      if (e.code === "invalid-argument") {
        return new ImportResponseFailure(e.message)
      }
      return e
    }
  }

  /**
   * Copy user profile to the public record, making it available to all.
   *
   * @return {Promise<boolean>}
   */
  async makeProfilePublic() {
    try {
      await this.updateProfile({ data: { profileIsPublic: true } })
      return true
    } catch (e) {
      return false
    }
  }

  /**
   * Copy section of user profile to the public record, making it available to all.
   *
   * @return {Promise<boolean>}
   */
  async makeProfileSectionPublic(section) {
    try {
      await this.updateProfile({
        data: { [`profilePublicSections.${section}`]: true },
      })
      return true
    } catch (e) {
      return false
    }
  }

  /**
   * Delete public profile data and mark main profile flag as a private profile.
   *
   * @return {Promise<boolean>}
   */
  async makeProfilePrivate() {
    try {
      await this.updateProfile({ data: { profileIsPublic: false } })
      return true
    } catch (e) {
      return false
    }
  }

  /**
   * Delete public profile section data.
   *
   * @return {Promise<boolean>}
   */
  async makeProfileSectionPrivate(section) {
    try {
      await this.updateProfile({
        data: { [`profilePublicSections.${section}`]: false },
      })
      return true
    } catch (e) {
      return false
    }
  }

  /**
   * Load a public user profile if the user has set it to public.
   *
   * @param userId
   * @return {Promise<*>}
   */
  async loadPublicProfile(userId) {
    const loadPublicProfileCallable = this.functions.httpsCallable("loadPublicProfile")
    const callableResult = await loadPublicProfileCallable({ userId: userId })
    return callableResult.data
  }

  async deleteUser(uid) {
    const deleteUser = this.functions.httpsCallable("deleteUser")
    return await deleteUser({ userId: uid })
  }

  async initializeUser(user) {
    // if (!user.uid) {
    //   return false
    // }

    this.createFirestoreUser(user)
  }

  /**
   * Gets resume data from firestore based on id.
   *
   * @param {string} planId
   *   The resume document ID.
   *
   * @return {Promise<null|T>}
   */
  async getResumeData() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    try {
      const query = await this.db.collection("userResumes").doc(user.uid).get()
      if (query.exists) {
        return query.data()
      } else {
        return null
      }
    } catch {
      return null
    }
  }

  async getResumeDataForStudents(userId) {
    if (!userId) {
      return null
    }

    try {
      const query = await this.db.collection("userResumes").doc(userId).get()
      if (query.exists) {
        return query.data()
      } else {
        return null
      }
    } catch {
      return null
    }
  }

  /**
   * Saves a resume.
   *
   * @param {CareerPlan} resume
   *
   * @return {Promise<void>}
   */
  async editResumeExperience(experience) {
    const editResumeExperienceCallable = this.functions.httpsCallable("editResumeExperience")
    const callableResult = await editResumeExperienceCallable({
      experience: experience,
    })
    const result = callableResult.data

    // resume.id = result.id
    // resume.updated = result.updated
    // if (result.created) {
    //   // resume.created = result.created
    // }
  }

  /**
   * Saves a resume.
   *
   * @param {CareerPlan} resume
   *
   * @return {Promise<void>}
   */
  async createResumeExperience(experience) {
    const uuid = v4()
    const resumeCreateExperienceCallable = this.functions.httpsCallable("createResumeExperience")
    const callableResult = await resumeCreateExperienceCallable({
      experience: { id: uuid, ...experience },
    })
    const result = callableResult.data
    // resume.id = result.id
    // resume.updated = result.updated
    // if (result.created) {
    //   // resume.created = result.created
    // }
  }

  /**
   * Saves a resume.
   *
   * @param {CareerPlan} resume
   *
   * @return {Promise<void>}
   */
  async deleteResumeExperience(experienceId) {
    const deleteResumeExperienceCallable = this.functions.httpsCallable("deleteResumeExperience")
    const callableResult = await deleteResumeExperienceCallable({
      id: experienceId,
    })
    const result = callableResult.data

    // resume.id = result.id
    // resume.updated = result.updated
    // if (result.created) {
    //   // resume.created = result.created
    // }
  }

  /**
   * Given a resume bullet, returns 3 optimized versions
   *
   * @param {ResumeBullet} bullet
   *
   * @return {Promise<Array>}
   */
  async optimizeText(text, type, tense) {
    const optimizeBulletCallable = this.functions.httpsCallable("optimizeText")
    const callableResult = await optimizeBulletCallable({
      text: text,
      type: type,
      tense: tense,
    })

    const result = callableResult.data
    return result
  }

  /**
   * Loads all career plans for a user
   *
   * @param userId
   *  The uid of the firebase user.
   *
   * @return {Promise<null|[CareerPlan]>}
   */
  async loadCareerPlans(userId) {
    const careers = []
    try {
      const allCareersSnapshot = await this.db.collection("careerPlans").doc(userId).get()
      if (!allCareersSnapshot.exists) {
        return null
      }
      const allPlans = allCareersSnapshot.data().plans || []
      return allPlans.filter(p => p !== "sample")
    } catch {
      return null
    }
  }

  /**
   * Loads school data from firestore.
   *
   * @param id
   *   The school ID.
   *
   * @return {Promise<null|School>}
   *   The school data or null.
   */
  async loadSchool(id, locale) {
    try {
      const localeId = locale === "en-US" ? `${id}` : `${id}-es`
      const query = await this.db.collection("schools").doc(localeId).get()
      if (query.exists) {
        return new School(query.data())
      } else {
        return null
      }
    } catch (e) {
      return null
    }
  }

  /**
   * Loads major data from firestore.
   *
   * @param cip
   *   The major CIP.
   *
   * @return {Promise<null|Major>}
   *   The major data or null.
   */
  async loadMajor(cip) {
    try {
      const query = await this.db.collection("majors").doc(`${cip}`).get()
      if (query.exists) {
        return new Major(query.data())
      } else {
        return null
      }
    } catch (e) {
      return null
    }
  }

  updateUserEmail(email) {
    const user = this.auth.currentUser
    return user.updateEmail(email)
  }

  updateUserPassword(password) {
    const user = this.auth.currentUser
    return user.updatePassword(password)
  }

  updateProfile(record) {
    const user = this.auth.currentUser
    return this.db.collection("users").doc(user.uid).update(record.data)
  }

  /**
   * Dismisses the weekly Monday notification.
   *
   * @return {Promise}
   */
  dismissMondayNotification() {
    const user = this.auth.currentUser
    return this.db.collection("users").doc(user.uid).update({
      "dismissedNotifications.monday": this.app.firestore.FieldValue.serverTimestamp(),
    })
  }

  /**
   * Dismisses the 10 day story notification.
   *
   * @return {Promise}
   */
  dismissStoryTenDayNotification() {
    const user = this.auth.currentUser
    return this.db.collection("users").doc(user.uid).update({
      "dismissedNotifications.storyTenDay": this.app.firestore.FieldValue.serverTimestamp(),
    })
  }

  /**
   * Dismisses the 10 day login notification.
   *
   * @return {Promise}
   */
  dismissLoginTenDayNotification() {
    const user = this.auth.currentUser
    return this.db.collection("users").doc(user.uid).update({
      "dismissedNotifications.loginTenDay": this.app.firestore.FieldValue.serverTimestamp(),
    })
  }

  dismissNewContentNotification(userProfile, notification) {
    const user = this.auth.currentUser
    if (!userProfile?.dismissedNotifications?.newContent || userProfile?.dismissedNotifications?.newContent.length === 0) {
      return this.db
        .collection("users")
        .doc(user.uid)
        .update({
          "dismissedNotifications.newContent": [notification],
        })
    }
    let dismissals = userProfile.dismissedNotifications.newContent
    // Filter out any old dismissals that are out of timeframe
    dismissals = dismissals.filter((dismissedNotification) => Date.now() - dismissedNotification.createdDate.toMillis() <= newContentAlertTimeFrame)
    dismissals.push(notification)
    return this.db.collection("users").doc(user.uid).update({
      "dismissedNotifications.newContent": dismissals,
    })
  }

  /**
   * Delete the current user's profile image.
   */
  async deleteProfileImage() {
    try {
    const user = this.auth.currentUser
    const directoryRef = this.storage.ref(`profile-images/${user.uid}`)
    // There may be multiple profile images in the user's profile-images folder
    // if the user has used multiple file types for their profile image
    const directoryContents = await directoryRef.listAll()
    if (directoryContents.items && directoryContents.items.length > 0) {
      await Promise.all(directoryContents.items.map(async fileRef => {
        if (fileRef.name.startsWith("profile")) {
          await fileRef.delete()
        }
      }))
    }
    } catch (e) {
      console.log("error in deleteProfileImage", e)
    }
  }

  /**
   * Store a user's profile photo to Firebase Storage.
   *
   * @param {File} file
   * @return {Promise<null|string>} Firebase Download URL or null
   */
  async storeProfileImage(file) {
    // Properly configured Storage rules will validate file type and file size
    const user = this.auth.currentUser
    const extension = file.type.split("/")[1]

    if (!extension) {
      return null
    }

    const storageRef = this.storage.ref()
    const profileImageRef = storageRef.child(`profile-images/${user.uid}/profile.${extension}`)

    try {
      await this.deleteProfileImage()
      await profileImageRef.put(file)
    } catch (e) {
      console.error(e)
      return null
    }

    const fileUrl = await profileImageRef.getDownloadURL()
    if (fileUrl) {
      return fileUrl
    } else {
      return null
    }
  }

  /**
   * Delete's a user's wallpaper image
   */
  async deleteWallpaperImage() {
    try {
      const user = this.auth.currentUser
      const directoryRef = this.storage.ref(`profile-images/${user.uid}`)
      // There may be multiple wallpaper images in the user's profile-images folder
      // if the the user has used multiple file types for their wallpaper image
      const directoryContents = await directoryRef.listAll()
      if (directoryContents.items && directoryContents.items.length > 0) {
        await Promise.all(directoryContents.items.map(async fileRef => {
          if (fileRef.name.startsWith("wallpaper")) {
            await fileRef.delete()
          }
        }))
      }
    } catch (e) {
      console.log("error in deleteWallpaperImage", e)
    }
  }

  /**
   * Store a user's wallpaper photo to Firebase Storage.
   *
   * @param {File} file
   * @return {Promise<null|string>} Firebase Download URL or null
   */
  async storeWallpaperImage(file) {
    // Properly configured Storage rules will validate file type and file size
    const user = this.auth.currentUser
    const extension = file.type.split("/")[1]

    if (!extension) {
      return null
    }

    const storageRef = this.storage.ref()
    const wallpaperImageRef = storageRef.child(`profile-images/${user.uid}/wallpaper.${extension}`)

    try {
      await this.deleteWallpaperImage()
      await wallpaperImageRef.put(file)
    } catch (e) {
      console.error(e)
      return null
    }

    const fileUrl = await wallpaperImageRef.getDownloadURL()
    if (fileUrl) {
      return fileUrl
    } else {
      return null
    }
  }

  // async register({email, password, username}) {
  //   await this.auth.createUserWithEmailAndPassword(email, password);
  //   const createProfileCallable = this.functions.httpsCallable('createPublicProfile');
  //   return createProfileCallable({
  //     username
  //   })
  // }
  async register(user) {
    await this.auth.createUserWithEmailAndPassword(user.email, user.password)

    await this.initializeUser(user)

    // const createProfileCallable = this.functions.httpsCallable('createPublicProfile');
    // return createProfileCallable({
    //   username
    // })
  }

  /**
   * Creates a school based user
   *
   */
  async createSchoolUser(schoolId, locale, authResultDisplayName = "", isNameReversed = false, staffEmailIsMatch = false, premadeAccount = false) {
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname
      const createSchoolUserCallable = this.functions.httpsCallable("createSchoolUser")
      const callableResult = await createSchoolUserCallable({ schoolId, locale, authResultDisplayName, isNameReversed, staffEmailIsMatch, hostname, premadeAccount })
      return callableResult.data
    }
  }

  /**
   * Runs on SSO sign in, adds user to school_members SQL table if they are not already in it
   * This is required for promo code users to be associated to the proper school after creation
   *
   * @param {string} schoolId - The ID of the school to add the user to
   * @returns {Promise} The result of the cloud function call
   */
  async confirmUserInSchool(schoolId) {
    try {
      const confirmUserInSchoolCallable = this.functions.httpsCallable("confirmUserInSchool")
      const callableResult = await confirmUserInSchoolCallable({ schoolId })
      return callableResult.data
    } catch (error) {
      console.error("Error confirming user in school:", error)
      throw error
    }
  }

  // async postComment({text, bookId}){
  //   const postCommentCallable = this.functions.httpsCallable('postComment');
  //   return postCommentCallable({
  //     text,
  //     bookId
  //   });
  // }

  async logout() {
    if (this.auth.currentUser?.uid) {
      localStorage.removeItem(`wings-session:${this.auth.currentUser.uid}`)
    }
    await this.auth.signOut()
  }

  /**
   * Adds login count to user profile.
   *
   * This will only increase login count if the user does not have a session
   * in local storage. Otherwise at least 24 hours needs to pass for a new
   * login to register. This is due to firebase and google auth automatically
   * logging users in and generating new tokens on hard refresh which would
   * result in a large number of login events.
   *
   * @param {string} uid
   *   The user id.
   *
   * @return {Promise<void>}
   */
  async loginCount(uid) {
    const wingsSessionKey = `wings-session:${this.auth.currentUser.uid}`
    const sessionExpiration = localStorage.getItem(wingsSessionKey)
    if (sessionExpiration && Date.now() < sessionExpiration) {
      return
    }
    localStorage.setItem(wingsSessionKey, Date.now() + sessionExpirationTime)

    try {
      // must occur on a delay in order for firebase user to exist upon account creation first login
      setTimeout(async () => {
        try {
          await this.db
            .collection("users")
            .doc(uid)
            .update({
              loginCount: this.app.firestore.FieldValue.increment(1),
              lastLoginDate: this.app.firestore.FieldValue.serverTimestamp(),
            })
        } catch (e) {
          console.log(e)
          console.error(e)
        }
      }, 20000)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Handles storing contact form submissions to Firestore.
   *
   * @param formValues
   * @returns {Promise<HttpsCallableResult>}
   */
  async storeContactFormSubmission(formValues) {
    const storeSubmission = this.functions.httpsCallable("storeContactFormSubmission")
    return storeSubmission(formValues)
  }

  /**
   * Handles storing form submission from /signup/employers to Firestore.
   *
   * @param formValues
   * @returns {Promise<HttpsCallableResult>}
   */
  async storeCompanySignUpFormSubmission(formValues) {
    const storeSubmission = this.functions.httpsCallable("storeCompanySignUpFormSubmission")
    return storeSubmission(formValues)
  }

  /**
   * Handles storing form submission from /signup/educators to Firestore.
   *
   * @param formValues
   * @returns {Promise<HttpsCallableResult>}
   */
  async storeEducatorSignUpFormSubmission(formValues) {
    const storeSubmission = this.functions.httpsCallable("storeEducatorSignUpFormSubmission")
    return storeSubmission(formValues)
  }

  /**
   * send email
   *
   * @param formValues
   * @returns {Promise<HttpsCallableResult>}
   */
  async sendEmail(formValues) {
    const sendEmail = this.functions.httpsCallable("sendEmail")
    return sendEmail(formValues)
  }

  async loadDashbardNotifications() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }
    const notifications = []

    try {
      const snapshot = await this.db.collection("notifications").where("userId", "==", user.uid).get()

      if (!snapshot.empty) {
        snapshot.forEach((doc) => {
          notifications.push({
            ...doc.data(),
            docId: doc.id,
          })
        })
      }

      // Only add general notifications for time frame.
      const timeFrame = new Date(Date.now() - newContentAlertTimeFrame)

      // 1 hour delay for new content notifications
      const hourDelay = new Date(Date.now() - 3600000)

      const snapshotGeneral = await this.db.collection("generalNotifications").where("createdDate", ">", timeFrame).get()

      if (!snapshotGeneral.empty) {
        snapshotGeneral.forEach((doc) => {
          if (doc.data().createdDate.toDate() < hourDelay || doc.data().type !== "newContent") {
            notifications.push({
              ...doc.data(),
              docId: doc.id,
            })
          }
        })
      }

      return notifications.length > 0 ? notifications : null
    } catch (e) {
      console.error("☠️", e)
      return null
    }
  }

  async removeDashboardNotifications(data) {
    // A specific document is required.
    if (!data.docId) {
      return
    }

    const user = this.auth.currentUser
    if (!user) {
      return false
    }

    try {
      await this.db.collection("notifications").doc(data.docId).delete()
      return true
    } catch (e) {
      console.error("☠️ removeDashboardNotifications ☠️", e)
      return false
    }
  }

  async createDashboardNotification(data) {
    const createDashboardNotification = this.functions.httpsCallable("createDashboardNotification")
    return createDashboardNotification(data)
  }

  addAssessmentCareersFeedback(data) {
    const addAssessmentCareersFeedback = this.functions.httpsCallable("handleAssessmentCareersFeedback")
    return addAssessmentCareersFeedback({ ...data, isRemoval: false })
  }

  async removeAssessmentCareersFeedback(data) {
    const removeAssessmentCareersFeedback = this.functions.httpsCallable("handleAssessmentCareersFeedback")
    return removeAssessmentCareersFeedback({ ...data, isRemoval: true })
  }

  async saveAssessment(data) {
    const saveAssessment = this.functions.httpsCallable("saveAssessment")
    return saveAssessment(data)
  }

  async saveAssessmentResponses(data) {
    const saveAssessmentResponses = this.functions.httpsCallable("saveAssessmentResponses")
    return saveAssessmentResponses(data)
  }

  async savePointEvents(data) {
    const savePointEvents = this.functions.httpsCallable("savePointEvents")
    return savePointEvents(data)
  }

  async saveVisitedStory(data) {
    const saveVisitedStory = this.functions.httpsCallable("saveVisitedStory")
    return saveVisitedStory(data)
  }

  async loadVisitedStories() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    try {
      const snapshot = await this.db.collection("visitedStories").where("userId", "==", user.uid).get()

      if (snapshot.empty) {
        return null
      } else {
        const stories = []
        snapshot.forEach((doc) => {
          stories.push({
            ...doc.data(),
            docId: doc.id,
          })
        })

        return stories
      }
    } catch (e) {
      console.error("☠️", e)
      return null
    }
  }

  async saveVisitedBlog(data) {
    const saveVisitedBlog = this.functions.httpsCallable("saveVisitedBlog")
    return saveVisitedBlog(data)
  }

  async loadVisitedBlogs() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    try {
      const snapshot = await this.db.collection("visitedBlogs").where("userId", "==", user.uid).get()

      if (snapshot.empty) {
        return null
      } else {
        const blogs = []
        snapshot.forEach((doc) => {
          blogs.push({
            ...doc.data(),
            docId: doc.id,
          })
        })

        return blogs
      }
    } catch (e) {
      console.error("☠️", e)
      return null
    }
  }

  async saveVisitedSchool(data) {
    const saveVisitedSchool = this.functions.httpsCallable("saveVisitedSchool")
    return saveVisitedSchool(data)
  }

  async loadVisitedSchools() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    try {
      const snapshot = await this.db.collection("visitedSchools").where("userId", "==", user.uid).get()

      if (snapshot.empty) {
        return null
      } else {
        const schools = []
        snapshot.forEach((doc) => {
          schools.push({
            ...doc.data(),
            docId: doc.id,
          })
        })
        return schools
      }
    } catch (e) {
      console.error("☠️", e)
      return null
    }
  }

  /**
   * Retrieve the current user's pointTotal as a static object, rather
   * than subscribing to the data.
   *
   * @return {Promise<null|*>}
   */
  async getPointTotal() {
    const user = this.auth.currentUser
    try {
      const snapshot = await this.db.collection("pointTotals").doc(user.uid).get()

      if (!snapshot.exists) {
        return null
      } else {
        return snapshot.data()
      }
    } catch (e) {
      return null
    }
  }

  async loadPointEvents() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    try {
      const snapshot = await this.db.collection("pointEvents").where("userId", "==", user.uid).get()

      if (snapshot.empty) {
        return null
      } else {
        const pointEvents = []
        snapshot.forEach((doc) => {
          pointEvents.push({
            ...doc.data(),
            docId: doc.id,
          })
        })

        return pointEvents
      }
    } catch (e) {
      console.error("☠️", e)
      return null
    }
  }

  /**
   * Saves walkthrough steps.
   *
   * @param {stepKey, stepValue}
   *
   * @return {Promise<void>}
   */
  async saveWalkthroughStep(stepKey, stepValue) {
    const saveWalkthroughStepCallable = this.functions.httpsCallable("saveWalkthroughStep")
    const callableResult = await saveWalkthroughStepCallable({
      stepKey,
      stepValue,
    })
    const result = callableResult.data
    return result
  }

  /**
   * Saves sample goal completion data for the current user.
   *
   * @param {{ complete?: boolean, stepKey?: string, stepAction?: "add"|"remove" }} payload
   * @returns {Promise<*>}
   */
  async saveUserSampleGoal(payload = {}) {
    const user = this.auth.currentUser
    if (!user) return false

    const saveUserSampleGoalCallable = this.functions.httpsCallable("saveUserSampleGoal")
    const callableResult = await saveUserSampleGoalCallable(payload)
    return callableResult.data
  }

  /**
   * Retrieves sample goal completion data for the current user.
   *
   * @returns {Promise<{ complete: boolean, completedStepKeys: string[] }|null>}
   */
  async getUserSampleGoal() {
    const user = this.auth.currentUser
    if (!user) {
      return {
        complete: false,
        completedStepKeys: [],
      }
    }

    const getUserSampleGoalCallable = this.functions.httpsCallable("getUserSampleGoal")
    const callableResult = await getUserSampleGoalCallable()
    return callableResult.data
  }

  async getWalkthroughSteps() {
    const user = this.auth.currentUser
    if (!user) {
      return null
    }

    const doc = await this.db.collection("userWalkthroughs").doc(user.uid).get()
    if (doc.exists) {
      return doc.data()
    } else {
      return null
    }
  }

  // saves user story rating to firestore, cosmic, and algolia
  async saveUserStoryRating(data) {
    const saveUserStoryRating = this.functions.httpsCallable("saveUserStoryRating")
    return saveUserStoryRating(data)
  }

  // gets users history of ratings across all stories
  async getUserStoryRatingsRecord(data) {
    const getUserStoryRatingsRecord = this.functions.httpsCallable("getUserStoryRatingsRecord")
    return getUserStoryRatingsRecord(data)
  }

  // gets record of all story ratings
  async getAllStoryMetrics() {
    const getAllStoryMetrics = this.functions.httpsCallable("getAllStoryMetrics")
    return getAllStoryMetrics()
  }

  // saves user activity completion
  async setActivityCompletion(data) {
    const setActivityCompletion = this.functions.httpsCallable("setActivityCompletion")
    return setActivityCompletion(data)
  }

  // removes user activity completion
  async unsetActivityCompletion(data) {
    const unsetActivityCompletion = this.functions.httpsCallable("unsetActivityCompletion")
    return unsetActivityCompletion(data)
  }

  // saves user activity completion
  async setDailyQuest(data) {
    const setDailyQuest = this.functions.httpsCallable("setDailyQuest")
    return setDailyQuest(data)
  }

  /**
   * Creates a new user via the magic link.
   */
  async createMagicLinkUser(data) {
    const createMagicLinkUserCallable = this.functions.httpsCallable("createMagicLinkUser")
    const callableResult = await createMagicLinkUserCallable(data)
    return callableResult.data
  }

  /**
   * Generate and send sign in link.
   */
  async generateAndSendSignInLink(data) {
    const generateAndSendSignInLinkCallable = this.functions.httpsCallable("generateAndSendSignInLink")
    await generateAndSendSignInLinkCallable(data)
  }

  /**
   * Send client side event details to GCP logs.
   * @param {string} message
   * @param {string} severity - info, warning, error
   * @returns {Promise<HttpsCallableResult>}
   */
  async logClientSideEvent(message, severity = "info") {
    const data = {
      message,
      severity,
    }
    const logClientSideEvent = this.functions.httpsCallable("logClientSideEvent")
    return logClientSideEvent(data)
  }

  /**
   * creates user notification, can be used for to trigger notification based on template ID or custom notification composed in client
   * @param {object} data.notificationData - object custom notification data
   * @param {string} data.notificationData - string template ID for notification
   */
  async createUserNotification(data) {
    const createUserNotification = this.functions.httpsCallable("createUserNotification")
    return createUserNotification({ notificationData: data })
  }

  /**
   * creates user notification, can be used for to trigger notification based on template ID or custom notification composed in client
   * @param {object} data.notificationData - object custom notification data
   * @param {string} data.notificationData - string template ID for notification
   */
  async createEducatorNotification(data) {
    const createEducatorNotification = this.functions.httpsCallable("createEducatorNotification")
    return createEducatorNotification({ notificationData: data })
  }

  /**
   * admin function for creating global notification
   */
  async createGlobalNotification(data) {
    const createGlobalNotification = this.functions.httpsCallable("createGlobalNotification")
    return createGlobalNotification(data)
  }

  /**
   * admin function for deleting notification globally by template ID
   */
  async deleteGlobalNotification(data) {
    const deleteGlobalNotification = this.functions.httpsCallable("deleteGlobalNotification2")
    return deleteGlobalNotification(data)
  }

  /**
   * Marks all notifications as read. Happens when notification drawer is opened.
   */
  async markUserNotificationsAsRead() {
    const markUserNotificationsAsRead = this.functions.httpsCallable("markUserNotificationsAsRead")
    return markUserNotificationsAsRead()
  }

  /**
   * Deletes a user notification, hides permanent notifications.
   *
   * @param {string} data.notificationId - the notification ID
   */
  async deleteUserNotification(data) {
    const deleteUserNotification = this.functions.httpsCallable("deleteUserNotification")
    return deleteUserNotification(data)
  }

  /**
   * Publishes a user notification
   *
   * @param {string} data.notificationId - the notification ID
   */
  async publishScheduledUserNotification(data) {
    const publishScheduledUserNotification = this.functions.httpsCallable("publishScheduledUserNotification")
    return publishScheduledUserNotification(data)
  }

  async logUserSiteVisit(data) {
    const logUserSiteVisit = this.functions.httpsCallable("logUserSiteVisit")
    return logUserSiteVisit({ siteVisitData: data })
  }

  async getEducatorAccountInfo() {
    const getEducatorAccountInfo = this.functions.httpsCallable("getEducatorAccountInfo")
    return getEducatorAccountInfo()
  }

  async getPetersonsSchoolData(institutionId) {
    const getPetersonsSchoolData = this.functions.httpsCallable("getPetersonsSchoolData")
    return getPetersonsSchoolData({ institutionId: institutionId })
  }

  async getPetersonsMajorSchoolsData(majorId) {
    const getPetersonsMajorSchoolsData = this.functions.httpsCallable("getPetersonsMajorSchoolsData")
    return getPetersonsMajorSchoolsData({ majorId: majorId })
  }

  /**
   * Creates a snapshot listener that responds to changes to a user's storyboard
   * @param {string} userId - the user's id. If this isn't defined, defaults to
   * the current user's id
   * @param {function} updatePanelOrder - function that runs when the order of the
   * storyboard panels changes
   * @returns the snapshot listener's cleanup function
   */
  subscribeToStoryboardChanges(userId, updatePanelOrder) {
    const onChange = (docRef) => {
      if (docRef.exists) {
        updatePanelOrder(docRef.data().panelsOrder || [])
      }
    }
    if (!userId) {
      userId = this.auth.currentUser.uid
    }
    return this.db
      .collection("userStoryboards")
      .doc(userId)
      .onSnapshot((docRef) => onChange(docRef))
  }

  /**
   * Creates a snapshot listener that responds to changes to a single storyboard panel
   * @param {string} userId - the user's id. If this isn't defined, defaults to
   * the current user's id
   * @param {string} panelId
   * @param {function} updateTitle
   * @param {function} updateDescription
   * @param {function} updateMonth
   * @param {function} updateYear
   * @param {function} updateFiles
   * @returns the snapshot listener's cleanup function
   */
  subscribeToPanelChanges(userId, panelId, updateTitle, updateDescription, updateMonth, updateYear, updateFiles) {
    const onChange = (docRef) => {
      if (docRef.exists) {
        updateTitle(docRef.data().title || "")
        updateDescription(docRef.data().description || "")
        updateMonth(docRef.data().endDateMonth || "")
        updateYear(docRef.data().endDateYear || "")
        updateFiles(docRef.data().filesOrder || [])
      }
    }
    if (!userId) {
      userId = this.auth.currentUser.uid
    }
    return this.db
      .collection("userStoryboards")
      .doc(userId)
      .collection("panels")
      .doc(panelId)
      .onSnapshot((docRef) => onChange(docRef))
  }

  /**
   * Update the order of a user's storyboard panels.
   *
   * Only one of panelID and newPanelsOrder should be defined.
   *
   * @param {string} panelID - the id of the panel to add or delete
   * @param {boolean} removePanel - if true, then remove panelId
   * @param {string[]} newPanelsOrder - the new order of the storyboard panels
   */
  async updatePanelsOrder(panelID, removePanel, newPanelsOrder) {
    const userId = this.auth.currentUser.uid
    const storyboardDocRef = this.db.collection("userStoryboards").doc(userId)
    const storyboardDoc = await storyboardDocRef.get()

    if (panelID) {
      let oldPanelsOrder = []
      if (storyboardDoc.exists && storyboardDoc.data().panelsOrder) {
        oldPanelsOrder = storyboardDoc.data().panelsOrder
      }
      if (removePanel) {
        const panelIndex = oldPanelsOrder.findIndex((id) => id === panelID)
        // TODO: best way to handle if panel ID isn't in this?
        oldPanelsOrder.splice(panelIndex, 1)
      } else {
        oldPanelsOrder.push(panelID)
      }
      newPanelsOrder = oldPanelsOrder
    }

    await storyboardDocRef.set({ panelsOrder: newPanelsOrder })
  }

  /**
   * Creates a new storyboard panel
   *
   * @param {string} title
   * @param {string} description
   * @param {string} month
   * @param {string} year
   * @param {string[]} filesOrder
   */
  async addPanel(title, description, month, year, filesOrder) {
    const newPanelID = v4()
    await this.updatePanelsOrder(newPanelID, false, undefined)
    await this.editPanel(newPanelID, false, title, description, month, year, filesOrder)
  }

  /**
   * Modify a single storyboard panel document in firestore
   *
   * @param {string} panelID
   * @param {boolean} deletePanel - if true, the panel will be deleted
   * @param {string} title
   * @param {string} description
   * @param {string} month
   * @param {string} year
   * @param {string[]} filesOrder
   */
  async editPanel(panelID, deletePanel, title, description, month, year, filesOrder) {
    const userID = this.auth.currentUser.uid
    const docRef = this.db.collection("userStoryboards").doc(userID).collection("panels").doc(panelID)

    if (deletePanel) {
      await docRef.delete()
    } else {
      const newPanel = {
        title: title || "",
        description: description || "",
        endDateMonth: month || "",
        endDateYear: year || "",
        filesOrder: filesOrder || [],
      }
      await docRef.set(newPanel)
    }
  }

  /**
   * Delete a storyboard panel
   * @param {*} panelID
   */
  async deletePanel(panelID) {
    await this.updatePanelsOrder(panelID, true, undefined)
    await this.editPanel(panelID, true, undefined, undefined, undefined, undefined, undefined)
  }

  /**
   * Upload a user's file
   * @param {string} purpose - Ex: "storyboard"
   * @param {string} fileID - a unique identifier for the file
   * @param {File} file
   * @param {File} thumbnail - if the file has a thumbnail, then this thumbnail
   * file will also be uploaded
   */
  async uploadUserFile(purpose, fileID, file, thumbnail) {
    if (!fileID) {
      fileID = v4()
    }
    const userID = this.auth.currentUser.uid
    const fileRef = this.storage.ref(`user-files/${userID}/${purpose}/${fileID}/${file.name}`)
    await fileRef.put(file)
    if (thumbnail) {
      const thumbnailRef = this.storage.ref(`user-files/${userID}/${purpose}/thumbnails/${fileID}`)
      await thumbnailRef.put(thumbnail)
    }
  }

  /**
   * Upload a user's file after clearing the target directory
   * @param {string} purpose - Ex: "curriculum"
   * @param {string} fileID - a unique identifier for the file
   * @param {File} file
   * file will also be uploaded
   */
  async uploadUserFileWithClear(purpose, fileID, file) {
    if (!fileID) {
      return Promise.reject(new Error("fileID is required"))
    }

    const userID = this.auth.currentUser.uid
    const folderPath = `user-files/${userID}/${purpose}/${fileID}`

    // Delete all existing files in the target folder
    try {
      const folderRef = this.storage.ref(folderPath)
      const folderContents = await folderRef.listAll()

      // Delete all existing files
      if (folderContents.items && folderContents.items.length > 0) {
        await Promise.all(folderContents.items.map((item) => item.delete()))
      }
    } catch (e) {
      console.error("No existing files to delete or error clearing folder:", e)
    }

    // Upload the new file
    const fileRef = this.storage.ref(`${folderPath}/${file.name}`)
    await fileRef.put(file)
  }

  /**
   * Get the MIME type of a user-uploaded file, or "" on error
   * @param {string} userId
   * @param {string} purpose - Ex: "storyboard"
   * @param {string} fileID - a unique identifier for the file
   * @returns {string}
   */
  async getUserFileType(userId, purpose, fileID) {
    if (!userId) {
      userId = this.auth.currentUser.uid
    }
    const directoryFileRef = this.storage.ref(`user-files/${userId}/${purpose}/${fileID}`)
    try {
      const directoryContents = await directoryFileRef.listAll()
      if (directoryContents.items && directoryContents.items.length > 0) {
        const fileRef = directoryContents.items[0]
        const metadata = await fileRef.getMetadata()
        return metadata.contentType
      }
    } catch (e) {
      console.error(e)
      return ""
    }
  }

  /**
   * Get a download URL for a user-uploaded file, or "" on error
   * @param {string} userId
   * @param {string} purpose - Ex: "storyboard"
   * @param {string} fileID - a unique identifier for the file
   * @returns {string}
   */
  async getUserFileURL(userId, purpose, fileID) {
    if (!userId) {
      userId = this.auth.currentUser.uid
    }
    try {
      const directoryFileRef = this.storage.ref(`user-files/${userId}/${purpose}/${fileID}`)
      const directoryContents = await directoryFileRef.listAll()
      if (directoryContents.items && directoryContents.items.length > 0) {
        const fileRef = directoryContents.items[0]
        const url = await fileRef.getDownloadURL()
        return url
      }
    } catch (e) {
      console.error(e)
      return ""
    }
  }

  /**
   * Get a download metadata for a user-uploaded file, or "" on error
   * @param {string} userId
   * @param {string} purpose - Ex: "storyboard"
   * @param {string} fileID - a unique identifier for the file
   * @returns {string}
   */
  async getUserFile(userId, purpose, fileID) {
    if (!userId) {
      userId = this.auth.currentUser.uid
    }
    try {
      const directoryFileRef = this.storage.ref(`user-files/${userId}/${purpose}/${fileID}`)
      const directoryContents = await directoryFileRef.listAll()
      if (directoryContents.items && directoryContents.items.length > 0) {
        const fileRef = directoryContents.items[0]
        const metadata = await fileRef.getMetadata()
        return metadata
      }
    } catch (e) {
      console.error(e)
      return ""
    }
  }

  /**
   * Get a download URL of a user-uploaded file's thumbnail, or "" on error
   * @param {string} userId
   * @param {string} purpose - Ex: "storyboard"
   * @param {string} fileID - a unique identifier for the file
   * @returns {string}
   */
  async getUserFileThumbnailURL(userId, purpose, fileID) {
    if (!userId) {
      userId = this.auth.currentUser.uid
    }
    try {
      const fileRef = this.storage.ref(`user-files/${userId}/${purpose}/thumbnails/${fileID}`)
      const url = await fileRef.getDownloadURL()
      return url
    } catch (e) {
      return ""
    }
  }

  /**
   * Delete a user uploaded file
   * @param {string} purpose - Ex: "storyboard"
   * @param {string} fileID - a unique identifier for the file
   */
  async deleteUserFile(purpose, fileID) {
    const userId = this.auth.currentUser.uid

    const directoryFileRef = this.storage.ref(`user-files/${userId}/${purpose}/${fileID}`)
    try {
      const directoryContents = await directoryFileRef.listAll()
      if (directoryContents.items && directoryContents.items.length > 0) {
        const fileRef = directoryContents.items[0]
        await fileRef.delete()
      }
    } catch (e) {
      console.error("failed to delete file", e)
    }

    const thumbnailRef = this.storage.ref(`user-files/${userId}/${purpose}/thumbnails/${fileID}`)
    try {
      await thumbnailRef.delete()
    } catch (e) {}
  }

  /**
   * Fetch the file IDs of all images the user has already uploaded for the
   * interactive curriculum
   * @returns {string[]}
   */
  async fetchCurriculumPreviousImages() {
    const userId = this.auth.currentUser.uid
    let output = []
    try {
      const directoryFileRef = this.storage.ref(`user-files/${userId}/curriculum-hosted-images`)
      const directoryContents = await directoryFileRef.listAll()
      if (directoryContents.prefixes) {
        output = directoryContents.prefixes.map((prefix) => prefix.name)
      }
    } catch (e) {
      console.error(e)
    }
    return output
  }

  /**
   * Fetch the URLs of the images in the preselected image library. Returns
   * them in a format compatible with the components used to display user files
   * @returns {userFile[]}
   */
  async fetchCurriculumImageLibrary() {
    let libraryImageFiles = []
    try {
      const libraryDirectoryRef = this.storage.ref(`interactive-curriculum-image-library`)
      const libraryImageRefs = await libraryDirectoryRef.listAll()
      if (libraryImageRefs.items) {
        const fetchFile = async (fileRef) => {
          const fileURL = await fileRef.getDownloadURL()
          const fileMetadata = await fileRef.getMetadata()
          const fileType = fileMetadata.contentType
          return {
            fileType: fileType,
            fileURL: fileURL,
            fileId: fileRef.name,
          }
        }
        libraryImageFiles = await Promise.all(libraryImageRefs.items.map(async (fileRef) => fetchFile(fileRef)))
      }
    } catch (e) {
      console.error(e)
    }
    return libraryImageFiles
  }

  /**
   * Create a snapshot listener that runs whenever the order of a user's resume
   * items changes
   * @param {string} userID
   * @param {string} columnNumber - either "1" or "2"
   * @param {function} updateOrder - takes in an array of experience type strings
   * @returns {function} the unsubscribe function
   */
  subscribeToResumeOrderChanges(userID, columnNumber, updateOrder) {
    const onChange = (docRef) => {
      if (docRef.exists && docRef.data().order) {
        updateOrder(docRef.data().order)
      } else {
        updateOrder([])
      }
    }
    return this.db
      .collection("userResumes")
      .doc(userID)
      .collection("columnOrders")
      .doc(columnNumber)
      .onSnapshot((docRef) => onChange(docRef))
  }

  /**
   * Update the order of experiences for a given resume column
   * @param {string} columnNumber - either "1" or "2"
   * @param {string[]} newOrder - array of experience type strings
   */
  async updateResumeColumnOrder(columnNumber, newOrder) {
    const userID = this.auth.currentUser.uid
    await this.db.collection("userResumes").doc(userID).collection("columnOrders").doc(columnNumber).set({ order: newOrder })
  }

  /**
   * Create a snapshot listener that runs whenever a user's resume experiences
   * change
   * @param {string} userID
   * @param {string} updateExperiences
   * @returns {function} the unsubscribe function
   */
  subscribeToResumeExperienceChanges(userID, updateExperiences) {
    const onChange = (docRef) => {
      if (docRef.exists && docRef.data().experiences) {
        updateExperiences(docRef.data().experiences)
      }
    }
    return this.db
      .collection("userResumes")
      .doc(userID)
      .onSnapshot((docRef) => onChange(docRef))
  }

  /**
   * Create a snapshot listener that runs when a user's skills or interests change
   * @param {string} userID
   * @param {function} updateSkills
   * @param {function} updateInterests
   * @returns {function} the snapshot listener's unsubscribe function
   */
  subscribeToSkillsInterestsChanges(userID, updateSkills, updateInterests) {
    const onChange = (docRef) => {
      if (docRef.exists) {
        updateSkills(docRef.data().skills || [])
      }
      if (docRef.exists) {
        updateInterests(docRef.data().interests || [])
      }
    }
    return this.db
      .collection("users")
      .doc(userID)
      .onSnapshot((docRef) => onChange(docRef))
  }

  /**
   * Get the experience type order that the current user has saved for a resume
   * column
   * @param {string} columnNumber - a string representing the column number (for
   * now, either "1" or "2")
   * @returns {string[]} array of experience types representing the order, or
   * null if no order is saved for the column
   */
  async getResumeColumnOrder(columnNumber) {
    const userId = this.auth?.currentUser?.uid
    if (!userId || !columnNumber) {
      return null
    }

    let order = null
    try {
      const orderDoc = await this.db.collection("userResumes").doc(userId).collection("columnOrders").doc(columnNumber).get()
      if (orderDoc.exists && orderDoc.data().order) {
        order = orderDoc.data().order
      }
    } catch (e) {
      console.error(e)
    }
    return order
  }

  async getAllProgramsWithUserProgress() {
    const getAllProgramsWithUserProgress = this.functions.httpsCallable("getAllProgramsWithUserProgress")
    return getAllProgramsWithUserProgress()
  }

  async getProgramByIdWithUserProgress(programId) {
    const getProgramByIdWithUserProgress = this.functions.httpsCallable("getProgramByIdWithUserProgress")
    return getProgramByIdWithUserProgress({ programId })
  }

  async getCertificateById(certificateId) {
    const getCertificateById = this.functions.httpsCallable("getCertificateById")
    try {
      const response = await getCertificateById({ certificateId })
      return response.data
    } catch (error) {
      console.error("Error getting certificate by ID:", error)
      throw error
    }
  }

  async getUserModuleRecord(moduleId, otherUserFirebaseUserId) {
    // const getUserModuleRecord = this.functions.httpsCallable("getUserModuleRecord")
    // return getUserModuleRecord({ moduleId, otherUserFirebaseUserId })

    try {
      const getUserModuleRecord = this.functions.httpsCallable("getUserModuleRecord")
      return await getUserModuleRecord({ moduleId, otherUserFirebaseUserId })
    } catch (err) {
      // Optionally log error: console.error('getUserModuleRecord error:', err)
      // Return a consistent error object or null
      console.error("getUserModuleRecord error:", err)
      return null
    }
  }

  async updateUserModuleById({ moduleId, moduleJson }) {
    const updateUserModuleById = this.functions.httpsCallable("updateUserModuleById")
    return updateUserModuleById({ moduleId, moduleJson })
  }

  async updateUserModuleStatus({ moduleId, status, score }) {
    const updateUserModuleStatus = this.functions.httpsCallable("updateUserModuleById")
    return updateUserModuleStatus({ moduleId, status: status, score: score })
  }

  async createUserModule(moduleId) {
    const createUserModule = this.functions.httpsCallable("createUserModule")
    return createUserModule({ moduleId, isRestart: false })
  }

  async createModule(moduleJson) {
    const createModule = this.functions.httpsCallable("createModule")
    return createModule(moduleJson)
  }

  async createProgram(programData) {
    const createProgram = this.functions.httpsCallable("createProgram")
    return createProgram({ programData: programData })
  }

  async updateProgram({ programId, programData }) {
    const createProgram = this.functions.httpsCallable("updateProgramById")
    return createProgram({ programId: programId, programData: programData })
  }

  async updateModuleById({ moduleJson, moduleId, accessType, isWingspansAdmin }) {
    const updateModuleById = this.functions.httpsCallable("updateModuleById")
    return updateModuleById({ moduleJson, moduleId, accessType, isWingspansAdmin })
  }

  async restartUserModule(moduleId) {
    const restartUserModule = this.functions.httpsCallable("createUserModule")
    return restartUserModule({ moduleId, isRestart: true })
  }

  async getAllStockModules(schoolCode) {
    const getAllStockModules = this.functions.httpsCallable("getAllStockModules")
    return getAllStockModules({ schoolCode: schoolCode || null })
  }

  async getAllStockAndCustomModules(schoolCode) {
    const getAllStockAndCustomModules = this.functions.httpsCallable("getAllStockAndCustomModules")
    return getAllStockAndCustomModules({ schoolCode: schoolCode || null })
  }

  async deleteModuleById(moduleId) {
    const deleteModuleById = this.functions.httpsCallable("deleteModuleById")
    return deleteModuleById({ moduleId })
  }

  async deleteProgramById(programId) {
    const deleteProgramById = this.functions.httpsCallable("deleteProgramById")
    return deleteProgramById({ programId })
  }

  async getCreatorDateMergedAssignments() {
    const getCreatorDateMergedAssignments = this.functions.httpsCallable("getCreatorDateMergedAssignments")
    return getCreatorDateMergedAssignments()
  }

  async getUsersAssignedToModuleOnDate({ moduleId, dueDate }) {
    const getUsersAssignedToModuleOnDate = this.functions.httpsCallable("getUsersAssignedToModuleOnDate")
    return getUsersAssignedToModuleOnDate({ moduleId, dueDate })
  }

  async getSubmissionDataByAssignmentId({ type, moduleAssignmentId, programAssignmentId}) {
    const getSubmissionDataByAssignmentId = this.functions.httpsCallable("getSubmissionDataByAssignmentId")
    return getSubmissionDataByAssignmentId({  type, moduleAssignmentId, programAssignmentId })
  }

  async getUsersAssignedToProgramOnDate({ programId, dueDate }) {
    const getUsersAssignedToProgramOnDate = this.functions.httpsCallable("getUsersAssignedToProgramOnDate")
    return getUsersAssignedToProgramOnDate({ programId, dueDate })
  }

  async createModuleAssignment({ assignedGroupId, assignedUserId, moduleId, dueDate, assigneeType, subdomain, domain, educatorName }) {
    const createModuleAssignment = this.functions.httpsCallable("createModuleAssignment")
    return createModuleAssignment({ assignedGroupId, assignedUserId, moduleId, dueDate, assigneeType, subdomain, domain, educatorName })
  }

  async createProgramAssignment({ assignedGroupId, assignedUserId, programId, dueDate, assigneeType, subdomain, domain, educatorName }) {
    const createProgramAssignment = this.functions.httpsCallable("createProgramAssignment")
    return createProgramAssignment({ assignedGroupId, assignedUserId, programId, dueDate, assigneeType, subdomain, domain, educatorName })
  }

  async getUserCompletedModulesRecord({ userId }) {
    const getUserCompletedModulesRecord = this.functions.httpsCallable("getUserCompletedModulesRecord")
    return getUserCompletedModulesRecord({ userId })
  }

  async getModulesAssignedToUser() {
    const getModulesAssignedToUser = this.functions.httpsCallable("getModulesAssignedToUser")
    return getModulesAssignedToUser()
  }

  async deleteModuleAssignmentsByIds(assignmentIds = []) {
    const deleteModuleAssignmentsByIds = this.functions.httpsCallable("deleteModuleAssignmentsByIds")
    return deleteModuleAssignmentsByIds({ assignmentIds })
  }

  async deleteProgramAssignmentsByIds(assignmentIds = []) {
    const deleteProgramAssignmentsByIds = this.functions.httpsCallable("deleteProgramAssignmentsByIds")
    return deleteProgramAssignmentsByIds({ assignmentIds })
  }

  async getPartnerSchools() {
    const getPartnerSchools = this.functions.httpsCallable("getPartnerSchools")
    return getPartnerSchools()
  }

  async getSchoolFilteredProgramsWithUserProgress(schoolCode) {
    const getSchoolFilteredProgramsWithUserProgress = this.functions.httpsCallable("getSchoolFilteredProgramsWithUserProgress")
    return getSchoolFilteredProgramsWithUserProgress({ schoolCode: schoolCode || null })
  }

  async getAllWingspansModules() {
    const getAllWingspansModules = this.functions.httpsCallable("getAllWingspansModules")
    return getAllWingspansModules()
  }

  /**
   * Records that a user visited a company page
   *
   * @param {string} companySlug - the slug of the company page being visited
   * @param {string} companyTitle - title of the company
   */
  async logCompanyPageVisit(companySlug, companyTitle) {
    // do nothing if the user isn't logged in (company pages are publicly visible)
    if (!this.auth?.currentUser?.uid) {
      return
    }

    const data = { slug: companySlug, title: companyTitle }
    const visitLogger = this.functions.httpsCallable("saveVisitedCompany")
    try {
      await visitLogger(data)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Records that current user clicked a company's website or social media link
   *
   * @param {string} companySlug - the slug of the company
   * @param {string} linkSlug - the type of link clicked, such as "website" or
   * "twitter"
   */
  async logCompanyLinkClick(companySlug, linkSlug) {
    if (!this.auth?.currentUser?.uid) {
      return
    }
    const data = {
      companySlug: companySlug,
      linkSlug: linkSlug,
    }
    const clickLogger = this.functions.httpsCallable("logCompanyLinkClick")
    try {
      await clickLogger(data)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Records that a user visited a school page
   *
   * @param {string} schoolSlug - the slug of the school page being visited
   * @param {string} schoolTitle - title of the school
   */
  async logSchoolPageVisit(schoolSlug, schoolTitle) {
    const data = { slug: schoolSlug, title: schoolTitle }
    const visitLogger = this.functions.httpsCallable("saveVisitedSchool")
    try {
      await visitLogger(data)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Records that a user clicked a link on a school page
   *
   * @param {string} schoolSlug - the slug of the school
   * @param {string} linkSlug - the type of link clicked, such as "website" or
   * "twitter"
   */
  async logSchoolLinkClick(schoolSlug, linkSlug) {
    const data = {
      schoolSlug: schoolSlug,
      linkSlug: linkSlug,
    }
    const clickLogger = this.functions.httpsCallable("logSchoolLinkClick")
    try {
      await clickLogger(data)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Fetch the names and peterson's IDs of all schools in our copy of peterson's
   * database
   *
   * @returns {Promise<{name: string, id: string}[]>} array of school data, or
   * [] on error
   */
  async getAllSchoolNamesAndIds() {
    const schoolDataFetcher = this.functions.httpsCallable("getAllSchoolNamesAndIds")
    try {
      const response = await schoolDataFetcher()
      return response.data || []
    } catch (e) {
      console.error("error in getAllSchoolNamesAndIds", e)
      return []
    }
  }

  /**
   * Fetch all degree programs associated with a school
   *
   * @param {string} schoolId - peterson's institution id of the school
   *
   * @returns {Promise<{
   *  cip_code: string,
   *  cip_title: string,
   *  cip_title_es: string,
   *  description: string,
   *  group_id: string,
   *  group_name: string,
   *  group_name_es: string,
   *  program_title: string,
   *  program_url: string,
   *  certificate: boolean,
   *  associates: boolean,
   *  bachelors: boolean,
   *  graduate: boolean,
   * }[]>} array of program data, or null on error
   */
  async getProgramsByPetersonsId(schoolId) {
    const requestData = {
      schoolId: schoolId,
    }
    try {
      const programDataFetcher = this.functions.httpsCallable("getProgramsByPetersonsId")
      const response = await programDataFetcher(requestData)
      return response.data || null
    } catch (e) {
      console.error("error in getProgramsByPetersonsId", e)
      return null
    }
  }

  /**
   * Fetch all CIP major classifications that are not currently associated with
   * a school
   *
   * @param {string} schoolId - peterson's institution id of the school
   *
   * @returns {Promise<{cip_code: string, cip_title: string, description: string}[]>}
   * an array of majors, or null on error
   */
  async getUnusedMajorsByPetersonsId(schoolId) {
    const requestData = {
      schoolId: schoolId,
    }
    try {
      const unusedMajorFetcher = this.functions.httpsCallable("getUnusedMajorsByPetersonsId")
      const response = await unusedMajorFetcher(requestData)
      return response.data || null
    } catch (e) {
      console.error("error in getUnusedMajorsByPetersonsId", e)
      return null
    }
  }

  /**
   * Update the program and degree level data we have stored for a school's program
   *
   * @param {{
   *  cip_code: string,
   *  program_title: string,
   *  program_url: string,
   *  certificate: boolean,
   *  associates: boolean,
   *  bachelors: boolean,
   *  graduate: boolean}} programData - edited program data
   * @param {string} schoolId - peterson's institution id of the school
   *
   * Expects the caller to catch any errors that occur when attempting the deletion
   */
  async editDegreeProgram(programData, schoolId) {
    const requestData = {
      school_id: schoolId,
      ...programData,
    }
    const programEditor = this.functions.httpsCallable("editDegreeProgram")
    await programEditor(requestData)
  }

  /**
   * Delete a major/program from a school
   *
   * @param {string} cip_code - CIP classification of the program
   * @param {string} school_id - peterson's institution ID of the school
   *
   * Expects the caller to catch any errors that occur when attempting to edit the data
   */
  async deleteDegreeProgram(cip_code, school_id) {
    const requestData = {
      cip_code: cip_code,
      school_id: school_id,
    }
    const programDeleter = this.functions.httpsCallable("deleteDegreeProgram")
    await programDeleter(requestData)
  }

  /**
   * Download a CSV containing all the program data (CIP classification, program
   * title, program URL, and degree levels) we currently have for a school.
   *
   * @param {string} school_id - peterson's institution id of the school
   * @param {boolean} scrape_programs - whether to search the internet for titles/URLs
   * for each of the school's programs and use that to populate the CSV
   *
   * @returns {Promise<string>} the csv data
   */
  async downloadDegreeProgramCsv(school_id, scrape_programs) {
    const requestData = {
      school_id: school_id,
      scrape_programs: scrape_programs,
    }
    try {
      // need to specify a longer timeout both here and on the backend
      const csvFetcher = this.functions.httpsCallable("downloadDegreeProgramCsv", { timeout: 540000 })
      const response = await csvFetcher(requestData)
      return response.data
    } catch (e) {
      console.error("error in downloadDegreeProgramCsv", e)
      return ""
    }
  }

  /**
   * Update or add any programs (CIP classification, degree levels offered, program
   * title, program URL) included in a CSV
   *
   * @param {string} csvData
   * @param {string} schoolId - peterson's institution id of the school
   * @param {boolean} deleteProgramsNotInCsv - whether to delete all programs
   * not included in the CSV
   *
   * Expects the caller to catch any errors that occur due to an improperly
   * formatted CSV. The error will include a relevant message for the user.
   *
   * @returns {Promise<number[]>} - the numbers of any rows that were not able
   * to be successfully imported despite being formatted correctly in the CSV
   */
  async importDegreeProgramCsv(csvData, schoolId, deleteProgramsNotInCsv) {
    const requestData = {
      csvData: csvData,
      schoolId: schoolId,
      deleteProgramsNotInCsv: deleteProgramsNotInCsv,
    }
    const importer = this.functions.httpsCallable("importDegreeProgramCsv")
    const response = await importer(requestData)
    return response.data || []
  }

  /**
   * Fetch all information about a client's tokens
   * @param {string} schCode - unique ID given to each client
   * @returns {*} or null on error
   */
  async getTokenDataBySchoolCode(schCode) {
    const getTokenData = this.functions.httpsCallable("getTokenDataBySchoolCode")
    try {
      const response = await getTokenData(schCode)
      return response.data
    } catch (e) {
      console.error("Error in getTokenDataBySchoolCode", e)
      return null
    }
  }

  /**
   * @param {string} token - the token value, or "" if a new token is being created
   * @param {string} name
   * @param {string} expirationDate - formatted YYYY-MM-DD
   * @param {string} schCode - the unique code given to each client school/org
   * @returns {string} the value of the edited token
   *
   * Expects caller to catch error messages
   */
  async editToken(token, name, expirationDate, schCode) {
    const request = {
      token: token,
      name: name,
      expirationDate: expirationDate,
      schCode: schCode,
    }
    const tokenEditor = this.functions.httpsCallable("editToken")
    const response = await tokenEditor(request)
    return response.data
  }

  /**
   * Check whether a school currently has the tokens feature enabled
   * @param {string} schCode - unique code given to every client school/org
   * @returns {boolean}
   */
  async checkIfClientHasTokensEnabled(schCode) {
    const request = {
      schCode: schCode,
    }
    try {
      const tokenChecker = this.functions.httpsCallable("checkIfClientHasTokensEnabled")
      const response = await tokenChecker(request)
      return response.data
    } catch (e) {
      console.error("error in checkIfClientHasTokensEnabled:", e)
      return false
    }
  }

  /**
   * Download a CSV containing the names and contact info of all prospective users
   * that have signed up using a school/org's tokens, the token used, and the
   * sign up date
   * @param {string} schCode - unique code given to every client school/org
   * @param {string} token - the token to fetch activations data for. If null, will
   * fetch activations data for all tokens
   * @returns {Blob} or null on error
   */
  async downloadTokenActivationsCSV(schCode, token) {
    const request = {
      schCode: schCode,
      token: token,
    }
    try {
      const csvDownloader = this.functions.httpsCallable("downloadTokenActivationsCsv")
      const response = await csvDownloader(request)
      const csvString = response.data
      return new Blob([csvString], { type: "text/csv" })
    } catch (e) {
      console.error("error in downloadTokenActivationsCSV", e)
      return null
    }
  }

  // expects caller to catch errors
  async editProspectiveStudentPage(schCode, bannerImageUrl, overview, stats, quickLinks) {
    const request = {
      schCode: schCode,
      bannerImageUrl: bannerImageUrl,
      overview: overview,
      stats: stats,
      quickLinks: quickLinks,
    }
    const saver = this.functions.httpsCallable("editProspectiveStudentPage")
    await saver(request)
  }

  // returns null on error
  async getProspectiveStudentPageEdits(schCode) {
    const request = {
      schCode: schCode,
    }
    try {
      const fetcher = this.functions.httpsCallable("getProspectiveStudentPageEdits")
      const response = await fetcher(request)
      return response?.data?.payload || null
    } catch (e) {
      console.error(e)
      return null
    }
  }

  async updateUserTaleResponses(taleResponses) {
    const updateUserTaleResponses = this.functions.httpsCallable("updateUserTaleResponses")
    return updateUserTaleResponses({ taleResponses })
  }

  async getUserTaleResponsesAndMatches() {
    const getUserTaleResponsesAndMatches = this.functions.httpsCallable("getUserTaleResponsesAndMatches")
    return getUserTaleResponsesAndMatches()
  }

  async processTaleSubmissionKeywordsAndSkills(taleResponses) {
    const processTaleSubmissionKeywordsAndSkills = this.functions.httpsCallable("processTaleSubmissionKeywordsAndSkills", {
      timeout: 3600000, // 60 minute client timeout
    })

    return processTaleSubmissionKeywordsAndSkills({ taleResponses })
  }

  async processTaleSubmissionCareers(taleResponses) {
    const processTaleSubmissionCareers = this.functions.httpsCallable("processTaleSubmissionCareers", {
      timeout: 3600000, // 60 minute client timeout
    })

    return processTaleSubmissionCareers({ taleResponses })
  }

  async processTaleSubmissionStory(taleResponses) {
    const processTaleSubmissionStory = this.functions.httpsCallable("processTaleSubmissionStory", {
      timeout: 600000, // 10 minute client timeout
    })

    return processTaleSubmissionStory({ taleResponses })
  }

  async suggestTasks(taleResponses) {
    const suggestTasks = this.functions.httpsCallable("suggestTasks", {
      timeout: 360000, // 6 minute client timeout
    })

    return suggestTasks({ taleResponses })
  }

  async submitTaleCareerRating(rating, slug) {
    const submitTaleCareerRating = this.functions.httpsCallable("submitTaleCareerRating")
    return submitTaleCareerRating({ rating, slug })
  }

  async regenerateTaleStory(){
    const regenerateTaleStory = this.functions.httpsCallable("regenerateTaleStory")

    return regenerateTaleStory()
  }

  async updateTaleStory(story) {
    const updateTaleStory = this.functions.httpsCallable("updateTaleStory")
    return updateTaleStory({ story })
  }
  
  /**
   * Get the download URL for an assessment question's audio. 
   * @param {string} storyName
   * @param {string} personalityType - realistic, investigative, artistic, 
   * conventional, social, or enterprising. Needed because stories can be reused
   * in multiple assessment questions, but a story will never be used to measure
   * the same personality type twice.
   * @param {string} locale - en-US or es-ES
   * @returns {Promise<string>} a firebase storage download URL, or an empty 
   * string on error
   */
  async getAssessmentAudioURL(storyName, personalityType, locale) {
    const nameSlug = storyName.toLowerCase().replaceAll(" ", "-").replaceAll(/[^a-z\-]/g, "")
    try {
      const audioFileRef = this.storage.ref(`audio/assessment/${locale === "en-US" ? "english" : "spanish"}/${nameSlug}-${personalityType}.mp3`)
      return await audioFileRef.getDownloadURL()
    } catch (e) {
      console.error(e)
      return ""
    }
  }

  /**
   * Construct a map from module id to objects with the following information 
   * about the corresponding module:
   * - *id*: the module's id
   * - *title*: the module's title
   * - *is_public*: whether the module could be started by/assigned to a user from 
   *                any organization
   * - *sch_codes_with_access*: for modules with access_type "school" or "private", 
   *                            the sch_codes of orgs that can access the module 
   * on error, returns an empty map
   * @returns {Promise<Map<number, {id: number, is_public: boolean, sch_codes_with_access: Set<string>, title: string}>>}
   */
  async getCurriculumAnalyticsModuleFilters() {
    const moduleIdToModuleData = new Map()
    try {
      const response = await this.functions.httpsCallable("getCurriculumAnalyticsModuleFilters")()
      const downloadedModules = response.data
      downloadedModules.forEach(module => {
        module.sch_codes_with_access = new Set(module.sch_codes_with_access || [])
        moduleIdToModuleData.set(module.id, module)
      })
    } catch (e) {
      console.error("Error downloading curriculum analytics module filters:", e)
    }
    return moduleIdToModuleData
  }

  /**
   * Construct a map from program id to objects with the following information 
   * about the corresponding program:
   * - *id*: the program's id
   * - *title*: the program's title
   * - *is_public*: whether the program could be started by/assigned to a user from 
   *                any organization
   * - *sch_codes_with_access*: for programs with access_type "private", the sch_codes
   *                            of orgs that can access the program 
   * on error, returns an empty map
   * @returns {Promise<Map<number, {id: number, is_public: boolean, module_order: number[], sch_codes_with_access: Set<string>, title: string}>>}
   */
  async getCurriculumAnalyticsProgramFilters() {
    const programIdToProgramData = new Map()
    try {
      const response = await this.functions.httpsCallable("getCurriculumAnalyticsProgramFilters")()
      const downloadedPrograms = response.data
      downloadedPrograms.forEach(program => {
        program.sch_codes_with_access = new Set(program.sch_codes_with_access || [])
        programIdToProgramData.set(program.id, program)
      })
    } catch (e) {
      console.error("Error downloading curriculum analytics program filters:", e)
    }
    return programIdToProgramData
  }

  /**
   * @param {string[]} orgCodes - sch_codes of selected organizations
   * @param {number[]} moduleIds 
   * @param {number[]} programIds 
   * @returns {Promise<XLSX.WorkBook>} xlsx workbook, or null on error
   */
  async exportCurriculumAnalytics(orgCodes, moduleIds, programIds) {
    const request = {
      orgCodes: orgCodes,
      moduleIds: moduleIds,
      programIds: programIds,
    }
    let workbook = null
    try {
      const exporter = this.functions.httpsCallable("exportCurriculumAnalytics")
      const response = await exporter(request)
      workbook = response.data || null
    } catch (e) {
      console.error(e)
    }
    return workbook
  }

  /**
   * Attempts to sign up a user with a token. On error, throws an error with a
   * relevant error message.
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} email
   * @param {string} password
   * @param {string} token
   * @param {string} schCode
   * @param {boolean} isGoogleSSO - whether the user is signing up with Google SSO
   */
  async signUpWithToken(firstName, lastName, email, password, token, schCode, isGoogleSSO) {
    const signUpFunction = this.functions.httpsCallable("signUpWithToken")
    const request = {
      firstName: firstName,
      lastName: lastName,
      email: email,
      password: password,
      token: token,
      schCode: schCode,
      isGoogleSSO: isGoogleSSO,
    }
    await signUpFunction(request)
  }

  /**
   * Generate jpeg thumbnail for a user's pdf, word, or powerpoint file. Expects
   * caller to catch errors
   * @param {File} file
   * @returns {File}
   */
  async generateFileThumbnail(file) {
    const idToken = await this.auth.currentUser.getIdToken()
    const requestBody = new FormData() // encode request as 'multipart/form-data'
    requestBody.append("fileToConvert", file)
    const requestOptions = {
      method: "POST",
      headers: { "Authorization": `Bearer ${idToken}` },
      body: requestBody,
    } 
    const thumbnailServiceURL = `https://generate-microsoft-file-thumbnail-${process.env.GATSBY_FIREBASE_MESSAGING_ID}.us-central1.run.app`
    const response = await fetch(thumbnailServiceURL, requestOptions)
    if (response.ok)  {
      const thumbnailBlob = await response.blob()
      return new File([thumbnailBlob], "thumbnail", {type: "image/jpeg"})
    } else {
      throw new Error(`${response.status}: ${await response.text()}`)
    }
  }

  /**
   * Check if an email is already in use as a secondary email (used by firebase
   * auth as an alternative sign-in method, but not as an identifier or for
   * account recovery) for some user. Expects caller to catch errors
   * @param {string} email
   * @returns {Promise<boolean>}
   */
  async checkIfEmailAlreadyUsedAsSecondary(email) {
    const request = {
      email: email,
    }
    const emailChecker = this.functions.httpsCallable("checkIfEmailAlreadyUsedAsSecondary")
    try {
      const response = await emailChecker(request)
      return response.data
    } catch (e) {
      console.error(`checkIfEmailAlreadyUsedAsSecondary: error when checking ${email}`, e)
      throw e
    }
  }


  /**
   * Fetches initial job market data for a given ONET code and zip code
   * @param {string} onetCode - The ONET occupation code
   * @param {string} zipCode - The zip code for location-based data
   * @param {string} careerTitle - The career title
   * @returns {Promise<Object>} Initial job market data
   */
  async getJobMarketDataInitial({onetCode, zipCode, careerTitle}) {
    const getJobMarketDataInitial = this.functions.httpsCallable("getJobMarketDataInitial")
    return getJobMarketDataInitial({ onetCode, zipCode, careerTitle })
  }

  /**
   * Fetches full job market data for a given ONET code and zip code
   * @param {string} onetCode - The ONET occupation code
   * @param {string} zipCode - The zip code for location-based data
   * @param {string} careerTitle - The career title
   * @returns {Promise<Object>} Full job market data
   */
  async getJobMarketDataFull({onetCode, zipCode, careerTitle}) {
    const getJobMarketDataFull = this.functions.httpsCallable("getJobMarketDataFull")
    return getJobMarketDataFull({ onetCode, zipCode, careerTitle })
  }

  /**
   * Fetches state-level job market data for a given ONET code and zip code
   * @param {string} onetCode - The ONET occupation code
   * @param {string} zipCode - The zip code for location-based data
   * @param {string} careerTitle - The career title
   * @returns {Promise<Object>} State-level job market data
   */
  async getJobMarketDataState({onetCode, zipCode, careerTitle}) {
    const getJobMarketDataState = this.functions.httpsCallable("getJobMarketDataState")
    return getJobMarketDataState({ onetCode, zipCode, careerTitle })
  }

  /**
   * Fetches internship-level job market data for a given ONET code and zip code
   * @param {string} onetCode - The ONET occupation code
   * @param {string} zipCode - The zip code for location-based data
   * @param {string} careerTitle - The career title
   * @returns {Promise<Object>} Internship-level job market data
   */
  async getJobMarketDataInternships({onetCode, zipCode, careerTitle}) {
    const getJobMarketDataInternships = this.functions.httpsCallable("getJobMarketDataInternships")
    return getJobMarketDataInternships({ onetCode, zipCode, careerTitle })
  }
  
  /**
   * Creates an excel spreadsheet with curriculum responses. Only one of the
   * function arguments is intended to be specified. Expects caller to catch
   * errors, which will contain a relevant error message.
   * @param {number[]} moduleIds - ids of modules to fetch all submissions to (in the order to display them)
   * @param {number} moduleAssignmentId - id of module assignment to fetch all submissions to
   * @param {number} programAssignmentId - id of program assignment to fetch all submissions to
   * @returns {Promise<Blob>} excel file in blob form
   */
  async downloadCurriculumResponsesSpreadsheet(moduleIds, moduleAssignmentId, programAssignmentId) {
    const request = {
      moduleIds: moduleIds,
      moduleAssignmentId: moduleAssignmentId,
      programAssignmentId: programAssignmentId,
    }
    const downloader = this.functions.httpsCallable("downloadCurriculumResponsesSpreadsheet", { timeout: 540000 })
    const response = await downloader(request)
    const base64Data = response.data
    const decoded = await fetch(`data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Data}`)
    const blob = await decoded.blob()
    return blob
  }

  /**
   * Fetches job opening data for a company. Expects caller to catch errors
   * @param {string} query - The query to pass to serp api
   * @param {string} location - formatted "<city>, <state abbreviation" 
   * @param {string} getFullResults - whether to fetch all results, or just one page (for faster response times)
   * @returns {Promise<Object[]>} array of job posting objects
   */
  async getCompanyJobPostings({query, location, getFullResults}) {
    const getCompanyJobPostings = this.functions.httpsCallable("getCompanyJobPostings")
    const response = await getCompanyJobPostings({ query, location, getFullResults })
    return response.data
  }

  /**
   * Creates a snapshot listener that responds to changes to a user's career plans.
   * If a user has not edited or deleted the sample plan, one entry in the returned
   * plans array will be the string "sample", which is a placeholder for the sample
   * plan.
   * @param {string} userId - The user's id. If this isn't defined, defaults to
   * the current user's id
   * @param {Function} updateCareerPlans - Runs whenever the user's career plans
   * change. Takes the most up to date version as an argument.
   * @param {Function} onError - Runs if there's an error in the listener.
   * @returns {Function} the snapshot listener's cleanup function
   */
  subscribeToCareerPlansChanges(userId, updateCareerPlans, onError) {
    if (!userId) {
      userId = this.auth.currentUser.uid
    }
    const onChange = (docRef) => {
      if (docRef.exists && Array.isArray(docRef.data().plans)) {
        updateCareerPlans(docRef.data().plans)
      } else {
        updateCareerPlans(["sample"])
      }
    }
    return this.db
      .collection("careerPlans")
      .doc(userId)
      .onSnapshot(onChange, onError)
  }

  /**
   * Updates the current user's career plans. Expects caller to catch errors.
   * @param {Object[]} careerPlans 
   * @returns {Promise<void>}
   */
  async updateCareerPlans(careerPlans) {
    const userId = this.auth.currentUser.uid
    const docRef = this.db.collection("careerPlans").doc(userId)
    await docRef.set({ plans: careerPlans })
  }
}

let firebaseInstance

function getFirebaseInstance() {
  if (!firebaseInstance) {
    firebaseInstance = new Firebase()
    return firebaseInstance
  } else if (firebaseInstance) {
    return firebaseInstance
  } else {
    return null
  }
}

export default getFirebaseInstance
